import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { fahrtenService } from '../../../services/fahrtenService';

const AddFahrtModal = ({ isOpen, onClose, onCreateFahrt }) => {
  const [formData, setFormData] = useState({
    vin: '',
    vehicle_make: '',
    vehicle_model: '',
    fahrt_datum: new Date().toISOString().split('T')[0], // Today's date
    fahrt_uhrzeit_beginn: new Date().toTimeString().slice(0, 5), // Current time
    fahrt_uhrzeit_ende: '',
    startziel: '',
    ueber: '',
    endziel: '',
    fahrtzweck: '',
    fahrer_name: '',
    fahrer_adresse: '46537,Dinslaken, HünxerStr.131',
    notes: '',
    kennzeichen: localStorage.getItem('fahrt_kennzeichen') || '' // Load from localStorage
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fahrtzweckOptions = [
    { value: 'innen-aussenreinigung', label: 'Innen-aussenreinigung' },
    { value: 'komplettaufbereitung', label: 'Komplettaufbereitung' },
    { value: 'smart-repair', label: 'Smart-repair' },
    { value: 'zurueck_gefahren_fertig_sauber', label: 'Zurück gefahren fertig mit Sauber machen' },
    { value: 'abholung_reinigung', label: 'Abholung für Reinigung' },
    { value: 'zurueck_gefahren_fertiger_pkw', label: 'Zurück gefahren fertiger PKW' }
  ];

  const fahrerOptions = [
    { value: 'Khatib, Ahmad', label: 'Khatib, Ahmad' },
    { value: 'Alkhatib, Abdullatif', label: 'Alkhatib, Abdullatif' },
    { value: 'Bakri, Amjad', label: 'Bakri, Amjad' }
  ];


  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        vin: '',
        vehicle_make: '',
        vehicle_model: '',
        fahrt_datum: new Date().toISOString().split('T')[0],
        fahrt_uhrzeit_beginn: new Date().toTimeString().slice(0, 5),
        fahrt_uhrzeit_ende: '',
        startziel: '',
        ueber: '',
        endziel: '',
        fahrtzweck: '',
        fahrer_name: '',
        fahrer_adresse: '46537,Dinslaken, HünxerStr.131',
        notes: '',
        kennzeichen: localStorage.getItem('fahrt_kennzeichen') || ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Save kennzeichen to localStorage when it changes
    if (field === 'kennzeichen') {
      localStorage.setItem('fahrt_kennzeichen', value);
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.vin?.trim()) {
      newErrors.vin = 'VIN ist erforderlich';
    } else if (formData.vin.length !== 17) {
      newErrors.vin = 'VIN muss 17 Zeichen lang sein';
    }

    if (!formData.fahrtzweck?.trim()) {
      newErrors.fahrtzweck = 'Fahrtzweck ist erforderlich';
    }

    if (!formData.fahrt_datum?.trim()) {
      newErrors.fahrt_datum = 'Fahrtdatum ist erforderlich';
    }

    if (!formData.fahrt_uhrzeit_beginn?.trim()) {
      newErrors.fahrt_uhrzeit_beginn = 'Startzeit ist erforderlich';
    }

    // Validate time format (HH:MM)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (formData.fahrt_uhrzeit_beginn && !timeRegex.test(formData.fahrt_uhrzeit_beginn)) {
      newErrors.fahrt_uhrzeit_beginn = 'Ungültiges Zeitformat (HH:MM)';
    }

    if (formData.fahrt_uhrzeit_ende && !timeRegex.test(formData.fahrt_uhrzeit_ende)) {
      newErrors.fahrt_uhrzeit_ende = 'Ungültiges Zeitformat (HH:MM)';
    }

    // Validate date format
    if (formData.fahrt_datum && !/^\d{4}-\d{2}-\d{2}$/.test(formData.fahrt_datum)) {
      newErrors.fahrt_datum = 'Ungültiges Datumsformat (YYYY-MM-DD)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Prepare fahrt data
      const fahrtData = {
        vin: formData.vin.toUpperCase(),
        vehicle_make: formData.vehicle_make || null,
        vehicle_model: formData.vehicle_model || null,
        fahrt_datum: formData.fahrt_datum,
        fahrt_uhrzeit_beginn: formData.fahrt_uhrzeit_beginn,
        fahrt_uhrzeit_ende: formData.fahrt_uhrzeit_ende || null,
        startziel: formData.startziel || null,
        ueber: formData.ueber || null,
        endziel: formData.endziel || null,
        fahrtzweck: formData.fahrtzweck,
        fahrer_name: formData.fahrer_name || null,
        fahrer_adresse: formData.fahrer_adresse || null,
        notes: formData.notes || null,
        status: 'pending', // Default status
        metadata: {}
      };

      // Use the onCreateFahrt prop to let the parent handle fahrt creation
      if (onCreateFahrt) {
        await onCreateFahrt(fahrtData);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating fahrt:', error);
      setErrors(prev => ({ ...prev, submit: error?.message || 'Fehler beim Erstellen der Fahrt' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">Fahrt manuell hinzufügen</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Fahrzeuginformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="VIN"
                type="text"
                placeholder="17-stellige Fahrzeug-Identifikationsnummer"
                value={formData.vin}
                onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
                error={errors.vin}
                required
                maxLength={17}
              />
              
              <Input
                label="Kennzeichen"
                type="text"
                placeholder="z.B. K-AB 1234"
                value={formData.kennzeichen}
                onChange={(e) => handleInputChange('kennzeichen', e.target.value.toUpperCase())}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Fahrzeugmarke"
                type="text"
                placeholder="z.B. BMW, Audi, Mercedes"
                value={formData.vehicle_make}
                onChange={(e) => handleInputChange('vehicle_make', e.target.value)}
              />
              
              <Input
                label="Fahrzeugmodell"
                type="text"
                placeholder="z.B. 3er, A4, C-Klasse"
                value={formData.vehicle_model}
                onChange={(e) => handleInputChange('vehicle_model', e.target.value)}
              />
            </div>
          </div>

          {/* Trip Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Fahrtinformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Fahrtdatum"
                type="date"
                value={formData.fahrt_datum}
                onChange={(e) => handleInputChange('fahrt_datum', e.target.value)}
                error={errors.fahrt_datum}
                required
              />
              
              <Input
                label="Startzeit"
                type="time"
                value={formData.fahrt_uhrzeit_beginn}
                onChange={(e) => handleInputChange('fahrt_uhrzeit_beginn', e.target.value)}
                error={errors.fahrt_uhrzeit_beginn}
                required
              />
              
              <Input
                label="Endzeit"
                type="time"
                value={formData.fahrt_uhrzeit_ende}
                onChange={(e) => handleInputChange('fahrt_uhrzeit_ende', e.target.value)}
                error={errors.fahrt_uhrzeit_ende}
              />
            </div>
          </div>

          {/* Route Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Streckeninformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Startziel"
                type="text"
                placeholder="Startort der Fahrt"
                value={formData.startziel}
                onChange={(e) => handleInputChange('startziel', e.target.value)}
              />
              
              <Input
                label="Über"
                type="text"
                placeholder="Zwischenstopps oder Route"
                value={formData.ueber}
                onChange={(e) => handleInputChange('ueber', e.target.value)}
              />
              
              <Input
                label="Endziel"
                type="text"
                placeholder="Zielort der Fahrt"
                value={formData.endziel}
                onChange={(e) => handleInputChange('endziel', e.target.value)}
              />
            </div>
            
            <div className="mt-4">
              <Select
                label="Fahrtzweck"
                options={fahrtzweckOptions}
                value={formData.fahrtzweck}
                onChange={(value) => handleInputChange('fahrtzweck', value)}
                error={errors.fahrtzweck}
                required
                placeholder="Zweck der Fahrt auswählen oder eingeben"
                searchable
                allowCustomValue
              />
            </div>
          </div>

          {/* Driver Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Fahrerinformationen</h3>
            <div className="grid grid-cols-1 gap-4">
              <Select
                label="Fahrername"
                options={fahrerOptions}
                value={formData.fahrer_name}
                onChange={(value) => handleInputChange('fahrer_name', value)}
                placeholder="Fahrer auswählen"
                searchable
                allowCustomValue
              />
            </div>
            
            <div className="mt-4">
              <Input
                label="Fahreradresse"
                type="text"
                placeholder="Vollständige Adresse des Fahrers"
                value={formData.fahrer_adresse}
                onChange={(e) => handleInputChange('fahrer_adresse', e.target.value)}
              />
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Zusätzliche Informationen</h3>
            <div className="mt-4">
              <Input
                label="Notizen"
                type="text"
                placeholder="Zusätzliche Bemerkungen oder Notizen"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </div>
          </div>

          {/* Error Display */}
          {errors.submit && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-sm text-destructive font-medium">Fehler beim Erstellen der Fahrt</span>
              </div>
              <p className="text-sm text-destructive mt-1">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Plus"
              iconPosition="left"
            >
              Fahrt hinzufügen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddFahrtModal;
